import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import Section from "./Section";
import "./FormBuilder.scss";
import axiosInstance from "../../axiosInstace";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import WarningPopup from "../WarningPopup/WarningPopup";
import ButtonWithIcon from "../ButtonwithIcon/ButtonWithIcon";
import { useAuth } from "../../contexts/AuthContext";
import { getQuestionTypeMeta, QUESTION_TYPE_LIST } from "./questionTypes";
import {
  getTestMaxScore,
  generateScoreBands,
  applyBandSync,
  normalizeAnswerType,
} from "./scoringUtils";

const blankGlobalInterp = () => ({
  interpretationId: null,
  title: "",
  minScore: null,
  maxScore: null,
  description: "",
  isNew: true,
  isChanged: false,
  isDeleted: false,
});
const blankGlobalRec = () => ({
  recommendationId: null,
  title: "",
  minScore: null,
  maxScore: null,
  recommendationText: "",
  isNew: true,
  isChanged: false,
  isDeleted: false,
});

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const parseDurationToHM = (raw) => {
  if (!raw) return { hours: 0, minutes: 0 };
  if (typeof raw === "object" && "seconds" in raw) {
    const s = Number(raw.seconds) || 0;
    return {
      hours: Math.floor(s / 3600),
      minutes: Math.floor((s % 3600) / 60),
    };
  }
  if (typeof raw === "string") {
    return {
      hours: raw.match(/(\d+)H/) ? parseInt(raw.match(/(\d+)H/)[1]) : 0,
      minutes: raw.match(/(\d+)M/) ? parseInt(raw.match(/(\d+)M/)[1]) : 0,
    };
  }
  return { hours: 0, minutes: 0 };
};

const buildISODuration = ({ hours, minutes }) => {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  return h === 0 && m === 0
    ? null
    : `PT${h > 0 ? h + "H" : ""}${m > 0 ? m + "M" : ""}`;
};

// Backend stores interpretation/recommendation text as flat strings, so any
// real newline the user types in a textarea is escaped to the literal
// two-character sequence "\n" before it goes over the wire on save.
const escapeNewlines = (str) =>
  typeof str === "string" ? str.replace(/\r\n|\r|\n/g, "\\n") : str;

const withEscapedDescription = (item) => ({
  ...item,
  description: escapeNewlines(item.description),
});

const withEscapedRecommendationText = (item) => ({
  ...item,
  recommendationText: escapeNewlines(item.recommendationText),
});

// Reverse of escapeNewlines — run on data coming back from the backend so a
// literal "\n" turns back into a real line break for the textarea to show.
const unescapeNewlines = (str) =>
  typeof str === "string" ? str.replace(/\\n/g, "\n") : str;

const withUnescapedDescription = (item) => ({
  ...item,
  description: unescapeNewlines(item.description),
});

const withUnescapedRecommendationText = (item) => ({
  ...item,
  recommendationText: unescapeNewlines(item.recommendationText),
});

// Keep only the answer fields supported by the current question API whenever
// options are sent to it.
const answerOptionPayload = (option, includeId = false) => ({
  ...(includeId && option.id ? { answerOptionId: option.id } : {}),
  answerText: option.answerText || "",
  score: Number(option.score) || 0,
});

// ─── BandCard (global interpretation + recommendation, paired by range) ───
const BandCard = ({
  range,
  onRangeChange,
  interp,
  onInterpChange,
  rec,
  onRecChange,
  onDelete,
  showDelete,
}) => (
  <div className="sr-band">
    <div className="sr-band__hd">
      <span className="sr-band__range">
        <input
          type="number"
          placeholder="0"
          title="Minimum score (inclusive)"
          value={range.minScore ?? ""}
          onChange={(e) => onRangeChange("minScore", e.target.value)}
        />
        <span>–</span>
        <input
          type="number"
          placeholder="10"
          title="Maximum score (inclusive)"
          value={range.maxScore ?? ""}
          onChange={(e) => onRangeChange("maxScore", e.target.value)}
        />
      </span>
      {showDelete && (
        <IconButtonInline onClick={onDelete} title="Remove this band" />
      )}
    </div>
    <div className="sr-band__cols">
      <div className="sr-band__col">
        <span className="sr-band__col-label">
          <Icon icon="mdi:text-box-outline" /> Interpretation
        </span>
        <input
          placeholder="Band title, e.g. High performer"
          value={interp.title || ""}
          onChange={(e) => onInterpChange("title", e.target.value)}
        />
        <textarea
          rows={2}
          placeholder="What this score range means…"
          value={interp.description || ""}
          onChange={(e) => onInterpChange("description", e.target.value)}
        />
      </div>
      <div className="sr-band__col">
        <span className="sr-band__col-label">
          <Icon icon="mdi:lightbulb-outline" /> Recommendation
        </span>
        <input
          placeholder="Band title, e.g. Keep it up"
          value={rec.title || ""}
          onChange={(e) => onRecChange("title", e.target.value)}
        />
        <textarea
          rows={2}
          placeholder="What the respondent should do…"
          value={rec.recommendationText || ""}
          onChange={(e) => onRecChange("recommendationText", e.target.value)}
        />
      </div>
    </div>
  </div>
);

const IconButtonInline = ({ onClick, title }) => (
  <button
    type="button"
    className="sr-del"
    onClick={onClick}
    title={title}
    aria-label={title}
  >
    <Icon icon="material-symbols-light:delete-outline" />
  </button>
);

// ─── Toggle ───────────────────────────────────────────────────────────────────
const Toggle = ({ label, hint, value, onChange }) => (
  <label className="tgl">
    <span className="tgl__copy">
      <span className="tgl__label">{label}</span>
      {hint && <span className="tgl__hint">{hint}</span>}
    </span>
    <button
      type="button"
      className={`tgl__switch ${value ? "tgl__switch--on" : ""}`}
      onClick={() => onChange(!value)}
      aria-pressed={value}
    >
      <span className="tgl__thumb" />
    </button>
  </label>
);

// ─── step config ────────────────────────────────────────────────────────────
const STEPS = [
  { key: "details", label: "Basic Details", icon: "mdi:card-text-outline" },
  { key: "questions", label: "Questions", icon: "mdi:format-list-checks" },
  { key: "scoring", label: "Scoring", icon: "mdi:chart-bell-curve-cumulative" },
  { key: "settings", label: "Settings", icon: "mdi:tune-variant" },
];

// ─── FormBuilder ──────────────────────────────────────────────────────────────
const FormBuilder = ({
  mode = "create",
  testId = null,
  onBack,
  // True when the test being edited has already been taken by at least one
  // person. Passed down from TestManagement after the person acknowledges
  // the "this may affect existing results" warning. We also fall back to
  // `testUsed` from the GET /tests/{id} response below (see loadExistingTest)
  // so this restriction still applies even if a caller forgets to pass the
  // prop through.
  isTestTaken = false,
}) => {
  const navigate = useNavigate();
  // Only ROLE_ADMIN users may choose between a USER test and a TEMPLATE
  // test; everyone else silently gets USER (see testDetails.testType,
  // its default below, and the safeguard in handleCreate's payload).
  const { user: currentUser } = useAuth();
  const isAdmin =
    currentUser?.role === "ROLE_ADMIN" ||
    currentUser?.roles?.includes?.("ROLE_ADMIN");
  // `testUsed` mirrors the backend's own signal that the test already has
  // responses; `restrictToExisting` is the single source of truth the rest
  // of this component uses to decide whether new questions/sections/bands
  // may be created. Once a test has responses, only edits to what already
  // exists are allowed — additions and removals are blocked so past
  // respondents' scores/interpretations stay meaningful.
  const [testUsed, setTestUsed] = useState(false);
  const restrictToExisting = isTestTaken || testUsed;
  const [showTakenNotice, setShowTakenNotice] = useState(false);

  const [testDetails, setTestDetails] = useState({
    testId: null,
    testTitle: "",
    description: "",
    linkExpiryDate: "",
    durationHours: 0,
    durationMinutes: 0,
    testAttemptType: "ONE_TIME",
    maxAttempts: 1,
    showResultsToUser: false,
    showScore: false,
    showRecommendation: false,
    showInterpretation: false,
    showSectionRecommendation: false,
    showSectionInterpretation: false,
    testCategoryId: "",
    testType: "USER",
    isChanged: false,
  });

  const [categories, setCategories] = useState([]);

  const [sections, setSections] = useState([]);
  const [globalInterpretations, setGlobalInterpretations] = useState([
    {
      interpretationId: null,
      title: "",
      minScore: null,
      maxScore: null,
      description: "",
      isNew: true,
      isChanged: false,
      isDeleted: false,
    },
  ]);
  const [globalRecommendations, setGlobalRecommendations] = useState([
    {
      recommendationId: null,
      title: "",
      minScore: null,
      maxScore: null,
      recommendationText: "",
      isNew: true,
      isChanged: false,
      isDeleted: false,
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [globalBandCount, setGlobalBandCount] = useState(3);

  // ── new: workspace UI state (presentation only, no business logic) ────────
  const [activeStep, setActiveStep] = useState("details");
  const [searchQuery, setSearchQuery] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [expandedOutlineSections, setExpandedOutlineSections] = useState(
    () => new Set(),
  );
  const [sidebarQuestionFocus, setSidebarQuestionFocus] = useState(null);
  const [titleEditing, setTitleEditing] = useState(false);
  const titleInputRef = useRef(null);
  // Whether the "set a time limit" toggle is on — presentation-only; the
  // real values still live in testDetails.durationHours/Minutes. Turning
  // the toggle off zeroes those out so an unset limit round-trips as none.
  const [durationEnabled, setDurationEnabled] = useState(false);
  // Which timeline steps the person has actually opened — used so Scoring
  // and Settings (which have no required fields) only show as "done" once
  // they've genuinely been looked at, not by default.
  const [visitedSteps, setVisitedSteps] = useState(() => new Set(["details"]));
  useEffect(() => {
    setVisitedSteps((prev) =>
      prev.has(activeStep) ? prev : new Set(prev).add(activeStep),
    );
  }, [activeStep]);

  // ── load existing ──────────────────────────────────────────────────────────
  const loadExistingTest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/tests/${testId}`,
      );
      const testData = res.data?.data;
      const dur = parseDurationToHM(
        testData.testDuration ?? testData.testduration,
      );
      setDurationEnabled(dur.hours > 0 || dur.minutes > 0);

      const alreadyUsed = !!(isTestTaken || testData.testUsed);
      setTestUsed(!!testData.testUsed);
      if (alreadyUsed) setShowTakenNotice(true);

      setTestDetails({
        testId: testData.testId || testId,
        testTitle: testData.testTitle || "",
        description: testData.description || "",
        linkExpiryDate: testData.linkExpiryDate
          ? new Date(testData.linkExpiryDate).toISOString().slice(0, 10)
          : "",
        durationHours: dur.hours,
        durationMinutes: dur.minutes,
        testAttemptType:
          testData.testAttemptType === "One Time"
            ? "ONE_TIME"
            : testData.testAttemptType === "Multiple"
              ? "MULTIPLE"
              : testData.testAttemptType === "Unlimited"
                ? "UNLIMITED"
                : testData.testAttemptType || "ONE_TIME",
        maxAttempts: testData.maxAttempts || 1,
        showResultsToUser: testData.showResultsToUser ?? true,
        showScore: testData.showScore ?? true,
        showRecommendation: testData.showRecommendation ?? true,
        showInterpretation: testData.showInterpretation ?? true,
        showSectionRecommendation: testData.showSectionRecommendation ?? true,
        showSectionInterpretation: testData.showSectionInterpretation ?? true,
        testCategoryId: testData.categoryId ?? testData.testCategoryId ?? "",
        testType: isAdmin ? testData.testType || "USER" : "USER",
        isChanged: false,
      });

      if (testData.sections?.length) {
        setSections(
          testData.sections.map((section) => ({
            ...section,
            isNew: false,
            isChanged: false,
            isDeleted: false,
            questions: (section.questions || []).map((q) => ({
              ...q,
              // Backend sends humanized display text here ("Single Choice"),
              // not the enum name Question.jsx's answerType checks expect
              // ("SINGLE_CHOICE") — normalize it or the options editor for
              // this question silently fails to render at all. See the
              // comment above normalizeAnswerType in scoringUtils.jsx.
              answerType: normalizeAnswerType(q.answerType, QUESTION_TYPE_LIST),
              isNew: false,
              isChanged: false,
              isDeleted: false,
              answerOptions: (q.answerOptions || []).map((option) => ({
                id: option.answerOptionId,
                answerText: option.answerText || "",
                score: Number(option.score) || 0,
                isNew: false,
                isChanged: false,
                isDeleted: false,
              })),
            })),
            sectionInterpretation: section.sectionInterpretation?.length
              ? section.sectionInterpretation.map((i) => ({
                  ...withUnescapedDescription(i),
                  id: i.sectionInterpretationId,
                  isNew: false,
                  isChanged: false,
                  isDeleted: false,
                }))
              : [
                  {
                    title: "",
                    minScore: 0,
                    maxScore: 0,
                    description: "",
                    isNew: true,
                  },
                ],
            sectionRecommendations: section.sectionRecommendations?.length
              ? section.sectionRecommendations.map((r) => ({
                  ...withUnescapedRecommendationText(r),
                  id: r.sectionRecommendationId,
                  isNew: false,
                  isChanged: false,
                  isDeleted: false,
                }))
              : [
                  {
                    title: "",
                    minScore: 0,
                    maxScore: 0,
                    recommendationText: "",
                    isNew: true,
                  },
                ],
          })),
        );
      }

      if (testData.interpretations?.length)
        setGlobalInterpretations(
          testData.interpretations.map((i) => ({
            ...withUnescapedDescription(i),
            isNew: false,
            isChanged: false,
            isDeleted: false,
          })),
        );

      if (testData.recommendations?.length)
        setGlobalRecommendations(
          testData.recommendations.map((r) => ({
            ...withUnescapedRecommendationText(r),
            isNew: false,
            isChanged: false,
            isDeleted: false,
          })),
        );
    } catch (err) {
      toast.error(
        `❌ Failed to load: ${err.response?.data?.message || err.message}`,
      );
    } finally {
      setLoading(false);
    }
  }, [testId, isTestTaken, isAdmin]);

  useEffect(() => {
    axiosInstance
      .get(`${process.env.REACT_APP_API_USER_URL}/test-categories`)
      .then((res) => setCategories(res.data?.data || []))
      .catch(() => toast.error("❌ Failed to load categories"));
  }, []);

  useEffect(() => {
    if (mode === "edit" && testId) loadExistingTest();
  }, [mode, testId, loadExistingTest]);

  // A brand-new test starts with one section already in place — building a
  // form with zero sections isn't a real option, so there's no reason to
  // make the person click "Add Section" just to get the first one.
  useEffect(() => {
    if (mode === "create") {
      setSections((p) => (p.length === 0 ? [makeBlankSection(0)] : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Global interpretations/recommendations render paired as one band per
  // index, so keep the two (visible) lists the same length — pads the
  // shorter one with a fresh blank entry rather than truncating.
  useEffect(() => {
    const visI = globalInterpretations.filter((x) => !x.isDeleted).length;
    const visR = globalRecommendations.filter((x) => !x.isDeleted).length;
    if (visI > visR) {
      setGlobalRecommendations((p) => [
        ...p,
        ...Array.from({ length: visI - visR }, blankGlobalRec),
      ]);
    } else if (visR > visI) {
      setGlobalInterpretations((p) => [
        ...p,
        ...Array.from({ length: visR - visI }, blankGlobalInterp),
      ]);
    }
  }, [globalInterpretations, globalRecommendations]);

  // ── field helpers ──────────────────────────────────────────────────────────
  const setField = (key, value) =>
    setTestDetails((p) => ({ ...p, [key]: value, isChanged: true }));

  const setShowResultsToUser = (value) =>
    setTestDetails((p) => ({
      ...p,
      showResultsToUser: value,
      ...(value && {
        showScore: true,
        showRecommendation: true,
        showInterpretation: true,
        showSectionRecommendation: true,
        showSectionInterpretation: true,
      }),
      isChanged: true,
    }));

  // ── paired band helpers ─────────────────────────────────────────────────
  // Global interpretations and recommendations are edited together as one
  // "band": the score range is shared and kept in sync across both lists,
  // while title/description text stays independent per list.
  const changeGlobalBandRange = (i, f, v) => {
    const numVal = v === "" ? null : Number(v);
    setGlobalInterpretations((p) =>
      applyBandSync(
        p.map((it, x) =>
          x === i ? { ...it, [f]: numVal, isChanged: !it.isNew } : it,
        ),
        i,
        f,
        numVal,
      ),
    );
    setGlobalRecommendations((p) =>
      applyBandSync(
        p.map((it, x) =>
          x === i ? { ...it, [f]: numVal, isChanged: !it.isNew } : it,
        ),
        i,
        f,
        numVal,
      ),
    );
  };

  const changeGlobalInterpText = (i, f, v) =>
    setGlobalInterpretations((p) => {
      const u = [...p];
      u[i] = { ...u[i], [f]: v, isChanged: !u[i].isNew };
      return u;
    });
  const changeGlobalRecText = (i, f, v) =>
    setGlobalRecommendations((p) => {
      const u = [...p];
      u[i] = { ...u[i], [f]: v, isChanged: !u[i].isNew };
      return u;
    });

  const addGlobalBand = () => {
    if (restrictToExisting) {
      toast.error("This test has responses — new score bands can't be added.");
      return;
    }
    setGlobalInterpretations((p) => [...p, blankGlobalInterp()]);
    setGlobalRecommendations((p) => [...p, blankGlobalRec()]);
  };

  const delGlobalBand = (i) => {
    if (restrictToExisting) {
      toast.error("This test has responses — score bands can't be removed.");
      return;
    }
    const interpItem = globalInterpretations[i];
    const recItem = globalRecommendations[i];
    setGlobalInterpretations((p) =>
      interpItem?.isNew
        ? p.filter((_, x) => x !== i)
        : p.map((it, x) => (x === i ? { ...it, isDeleted: true } : it)),
    );
    setGlobalRecommendations((p) =>
      recItem?.isNew
        ? p.filter((_, x) => x !== i)
        : p.map((it, x) => (x === i ? { ...it, isDeleted: true } : it)),
    );
  };

  // Auto-build N interpretation + N recommendation bands that evenly (and
  // contiguously) cover 0..testMaxScore, mirroring the per-section version
  // in Section.jsx. Existing bands are soft-deleted/dropped the same way
  // the manual delete buttons do it, then replaced with the fresh set.
  const autoGenerateGlobalBands = () => {
    if (restrictToExisting) {
      toast.error("This test has responses — bands can't be regenerated.");
      return;
    }
    const total = getTestMaxScore(sections);
    const bands = generateScoreBands(total, globalBandCount);

    const retireAll = (list) =>
      (list || []).flatMap((item) =>
        item.isDeleted
          ? [item]
          : item.isNew
            ? []
            : [{ ...item, isDeleted: true }],
      );

    setGlobalInterpretations((p) => [
      ...retireAll(p),
      ...bands.map((b) => ({
        interpretationId: null,
        title: "",
        minScore: b.minScore,
        maxScore: b.maxScore,
        description: "",
        isNew: true,
        isChanged: false,
        isDeleted: false,
      })),
    ]);
    setGlobalRecommendations((p) => [
      ...retireAll(p),
      ...bands.map((b) => ({
        recommendationId: null,
        title: "",
        minScore: b.minScore,
        maxScore: b.maxScore,
        recommendationText: "",
        isNew: true,
        isChanged: false,
        isDeleted: false,
      })),
    ]);
  };

  // ── section helpers ────────────────────────────────────────────────────────
  const makeBlankSection = (order) => ({
    sectionId: null,
    sectionTitle: "",
    sectionOrder: order,
    randomizeQuestions: false,
    questions: [],
    isNew: true,
    isChanged: false,
    isDeleted: false,
    sectionInterpretation: [
      {
        title: "",
        minScore: null,
        maxScore: null,
        description: "",
        isNew: true,
      },
    ],
    sectionRecommendations: [
      {
        title: "",
        minScore: null,
        maxScore: null,
        recommendationText: "",
        isNew: true,
      },
    ],
  });

  const addSection = () => {
    if (restrictToExisting) {
      toast.error("This test has responses — new sections can't be added.");
      return;
    }
    setSections((p) => [...p, makeBlankSection(p.length)]);
    setActiveStep("questions");
  };
  const updateSection = (index, updated) =>
    setSections((p) => {
      const u = [...p];
      u[index] = { ...updated, isChanged: !updated.isNew };
      return u;
    });
  const deleteSection = (index) => {
    if (restrictToExisting) {
      toast.error("This test has responses — sections can't be removed.");
      return;
    }
    const s = sections[index];
    if (s.isNew) setSections((p) => p.filter((_, i) => i !== index));
    else
      setSections((p) => {
        const u = [...p];
        u[index] = { ...s, isDeleted: true };
        return u;
      });
  };

  // ── delete test ────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_USER_URL}/tests/${testId}`,
      );
      toast.success("✅ Deleted");
      setShowDeleteConfirm(false);
      onBack ? onBack() : navigate("/testmanagement");
    } catch {
      toast.error("❌ Delete failed");
      setShowDeleteConfirm(false);
    }
  };

  // ── create ─────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const cleanedSections = sections
      .filter((s) => !s.isDeleted)
      .map((s) => ({
        ...s,
        questions: (s.questions || [])
          .filter((q) => !q.isDeleted)
          .map((q) => ({
            ...q,
            answerOptions: (q.answerOptions || [])
              .filter((o) => !o.isDeleted)
              .map((o) => answerOptionPayload(o)),
          })),
        sectionInterpretation: (s.sectionInterpretation || [])
          .filter((i) => !i.isDeleted)
          .map(withEscapedDescription),
        sectionRecommendations: (s.sectionRecommendations || [])
          .filter((r) => !r.isDeleted)
          .map(withEscapedRecommendationText),
      }));

    const payload = {
      ...testDetails,
      testduration: buildISODuration({
        hours: testDetails.durationHours,
        minutes: testDetails.durationMinutes,
      }),
      maxAttempts:
        testDetails.testAttemptType === "MULTIPLE"
          ? parseInt(testDetails.maxAttempts, 10) || 1
          : null,
      testCategoryId: testDetails.testCategoryId
        ? Number(testDetails.testCategoryId)
        : null,
      // Non-admins can never set TEMPLATE, even if testDetails got there
      // some other way — always fall back to USER for them.
      testType: isAdmin ? testDetails.testType || "USER" : "USER",
      sections: cleanedSections,
      interpretations: globalInterpretations
        .filter((i) => !i.isDeleted)
        .map(withEscapedDescription),
      recommendations: globalRecommendations
        .filter((r) => !r.isDeleted)
        .map(withEscapedRecommendationText),
    };

    const createUrl = `${process.env.REACT_APP_API_USER_URL}/tests/section/question/answeroption`;

    try {
      await axiosInstance.post(createUrl, payload);
      toast.success("✅ Test created!");
      setTimeout(() => {
        if (onBack) {
          onBack(true); // true = refresh list
        }
      }, 600);
    } catch (err) {
      toast.error(`❌ ${err.response?.data?.message || err.message}`);
    }
  };

  // ── update flow ────────────────────────────────────────────────────────────
  const handleDeletions = async () => {
    const dels = [];
    sections.forEach((s) => {
      if (s.isDeleted && !s.isNew && s.sectionId)
        dels.push(
          axiosInstance.delete(
            `${process.env.REACT_APP_API_USER_URL}/sections/${s.sectionId}`,
          ),
        );
      if (!s.isDeleted) {
        (s.questions || []).forEach((q) => {
          if (q.isDeleted && !q.isNew && q.questionId)
            dels.push(
              axiosInstance.delete(
                `${process.env.REACT_APP_API_USER_URL}/questions/${q.questionId}`,
              ),
            );
        });
        (s.sectionInterpretation || []).forEach((i) => {
          if (i.isDeleted && !i.isNew && i.id)
            dels.push(
              axiosInstance.delete(
                `${process.env.REACT_APP_API_USER_URL}/sectioninterpretations/${i.id}`,
              ),
            );
        });
        (s.sectionRecommendations || []).forEach((r) => {
          if (r.isDeleted && !r.isNew && r.id)
            dels.push(
              axiosInstance.delete(
                `${process.env.REACT_APP_API_USER_URL}/sectionrecommendations/${r.id}`,
              ),
            );
        });
      }
    });
    globalInterpretations.forEach((i) => {
      if (i.isDeleted && i.interpretationId)
        dels.push(
          axiosInstance.delete(
            `${process.env.REACT_APP_API_USER_URL}/interpretations/${i.interpretationId}`,
          ),
        );
    });
    globalRecommendations.forEach((r) => {
      if (r.isDeleted && r.recommendationId)
        dels.push(
          axiosInstance.delete(
            `${process.env.REACT_APP_API_USER_URL}/recommendations/${r.recommendationId}`,
          ),
        );
    });
    await Promise.all(dels);
  };

  // Note: test-level fields (title, category, attempt type, visibility, etc.)
  // are no longer PUT here. They're saved once, together with any new
  // sections/interpretations/recommendations, in handleTestDetailsSave()
  // below — see that function for why they were split out.
  const handleExistingUpdates = async () => {
    const upds = [];
    globalInterpretations.forEach((i) => {
      if (i.isChanged && i.interpretationId && !i.isDeleted)
        upds.push(
          axiosInstance.put(
            `${process.env.REACT_APP_API_USER_URL}/interpretations/${i.interpretationId}`,
            {
              title: i.title,
              minScore: i.minScore,
              maxScore: i.maxScore,
              description: escapeNewlines(i.description),
            },
          ),
        );
    });
    globalRecommendations.forEach((r) => {
      if (r.isChanged && r.recommendationId && !r.isDeleted)
        upds.push(
          axiosInstance.put(
            `${process.env.REACT_APP_API_USER_URL}/recommendations/${r.recommendationId}`,
            {
              title: r.title,
              minScore: r.minScore,
              maxScore: r.maxScore,
              recommendationText: escapeNewlines(r.recommendationText),
            },
          ),
        );
    });
    sections.forEach((s) => {
      if (!s.isDeleted && !s.isNew && s.isChanged && s.sectionId)
        upds.push(
          axiosInstance.put(
            `${process.env.REACT_APP_API_USER_URL}/sections/${s.sectionId}`,
            {
              sectionTitle: s.sectionTitle,
              sectionOrder: s.sectionOrder,
              randomizeQuestions: s.randomizeQuestions,
            },
          ),
        );
      if (!s.isDeleted) {
        (s.questions || []).forEach((q) => {
          if (!q.isDeleted && !q.isNew && q.isChanged && q.questionId)
            upds.push(
              axiosInstance.put(
                `${process.env.REACT_APP_API_USER_URL}/questions/${q.questionId}`,
                {
                  questionId: q.questionId,
                  questionText: q.questionText,
                  answerType: q.answerType,
                  questionOrder: q.questionOrder,
                  answerOptions: (q.answerOptions || [])
                    .filter((o) => !o.isDeleted)
                    .map((o) => answerOptionPayload(o, true)),
                },
              ),
            );
        });
        (s.sectionInterpretation || []).forEach((i) => {
          if (!i.isDeleted && !i.isNew && i.isChanged && i.id)
            upds.push(
              axiosInstance.put(
                `${process.env.REACT_APP_API_USER_URL}/sectioninterpretations/${i.id}`,
                {
                  title: i.title,
                  minScore: i.minScore,
                  maxScore: i.maxScore,
                  description: escapeNewlines(i.description),
                },
              ),
            );
        });
        (s.sectionRecommendations || []).forEach((r) => {
          if (!r.isDeleted && !r.isNew && r.isChanged && r.id)
            upds.push(
              axiosInstance.put(
                `${process.env.REACT_APP_API_USER_URL}/sectionrecommendations/${r.id}`,
                {
                  title: r.title,
                  minScore: r.minScore,
                  maxScore: r.maxScore,
                  recommendationText: escapeNewlines(r.recommendationText),
                },
              ),
            );
        });
      }
    });
    await Promise.all(upds);
  };

  const handleTestDetailsSave = async () => {
    const newSections = sections
      .filter((s) => !s.isDeleted)
      .map((s) => {
        if (s.isNew)
          return {
            sectionTitle: s.sectionTitle,
            sectionOrder: s.sectionOrder,
            randomizeQuestions: s.randomizeQuestions,
            questions: (s.questions || [])
              .filter((q) => !q.isDeleted)
              .map((q) => ({
                questionText: q.questionText,
                answerType: q.answerType,
                questionOrder: q.questionOrder,
                answerOptions: (q.answerOptions || [])
                  .filter((o) => !o.isDeleted)
                  .map((o) => answerOptionPayload(o)),
              })),
            sectionInterpretation: (s.sectionInterpretation || [])
              .filter((i) => !i.isDeleted)
              .map(withEscapedDescription),
            sectionRecommendations: (s.sectionRecommendations || [])
              .filter((r) => !r.isDeleted)
              .map(withEscapedRecommendationText),
          };
        const nq = (s.questions || []).filter((q) => q.isNew && !q.isDeleted);
        const ni = (s.sectionInterpretation || [])
          .filter((i) => i.isNew && !i.isDeleted)
          .map(withEscapedDescription);
        const nr = (s.sectionRecommendations || [])
          .filter((r) => r.isNew && !r.isDeleted)
          .map(withEscapedRecommendationText);
        if (nq.length || ni.length || nr.length)
          return {
            sectionId: s.sectionId,
            sectionTitle: s.sectionTitle,
            sectionOrder: s.sectionOrder,
            randomizeQuestions: s.randomizeQuestions,
            questions: nq.map((q) => ({
              questionText: q.questionText,
              answerType: q.answerType,
              questionOrder: q.questionOrder,
              answerOptions: (q.answerOptions || [])
                .filter((o) => !o.isDeleted)
                .map((o) => answerOptionPayload(o)),
            })),
            sectionInterpretation: ni,
            sectionRecommendations: nr,
          };
        return null;
      })
      .filter(Boolean);

    const newGI = globalInterpretations
      .filter((i) => i.isNew && !i.isDeleted)
      .map(withEscapedDescription);
    const newGR = globalRecommendations
      .filter((r) => r.isNew && !r.isDeleted)
      .map(withEscapedRecommendationText);

    // Single source of truth for the /tests/{id} PUT. This used to be split
    // across two separate PUT calls (one for changed top-level fields, one
    // for new nested items). Firing both meant whichever landed second — the
    // "new items" call — carried only title/description/linkExpiryDate and
    // silently wiped out testCategoryId, testAttemptType, maxAttempts,
    // testduration, and the visibility flags on the backend. Now there's
    // exactly one PUT, and it always carries the full field set, so nothing
    // gets clobbered regardless of which combination of things changed.
    if (
      testDetails.isChanged ||
      newSections.length ||
      newGI.length ||
      newGR.length
    ) {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_USER_URL}/tests/${testDetails.testId}`,
        {
          testId: testDetails.testId,
          testTitle: testDetails.testTitle,
          description: testDetails.description,
          linkExpiryDate: testDetails.linkExpiryDate,
          testduration: buildISODuration({
            hours: testDetails.durationHours,
            minutes: testDetails.durationMinutes,
          }),
          testAttemptType: testDetails.testAttemptType,
          maxAttempts:
            testDetails.testAttemptType === "MULTIPLE"
              ? parseInt(testDetails.maxAttempts, 10) || 1
              : null,
          testCategoryId: testDetails.testCategoryId
            ? Number(testDetails.testCategoryId)
            : null,
          // Same rule as create: only admins can set TEMPLATE, everyone
          // else always gets USER regardless of what's in local state.
          testType: isAdmin ? testDetails.testType || "USER" : "USER",
          showResultsToUser: testDetails.showResultsToUser,
          showScore: testDetails.showScore,
          showRecommendation: testDetails.showRecommendation,
          showInterpretation: testDetails.showInterpretation,
          showSectionRecommendation: testDetails.showSectionRecommendation,
          showSectionInterpretation: testDetails.showSectionInterpretation,
          sections: newSections,
          interpretations: newGI,
          recommendations: newGR,
        },
      );
    }
  };

  const resetChangeFlags = () => {
    setTestDetails((p) => ({ ...p, isChanged: false }));
    setGlobalInterpretations((p) =>
      p
        .filter((i) => !i.isDeleted)
        .map((i) => ({ ...i, isChanged: false, isNew: false })),
    );
    setGlobalRecommendations((p) =>
      p
        .filter((r) => !r.isDeleted)
        .map((r) => ({ ...r, isChanged: false, isNew: false })),
    );
    setSections(
      sections
        .filter((s) => !s.isDeleted)
        .map((s) => ({
          ...s,
          isChanged: false,
          isNew: false,
          questions: (s.questions || [])
            .filter((q) => !q.isDeleted)
            .map((q) => ({ ...q, isChanged: false, isNew: false })),
          sectionInterpretation: (s.sectionInterpretation || [])
            .filter((i) => !i.isDeleted)
            .map((i) => ({ ...i, isChanged: false, isNew: false })),
          sectionRecommendations: (s.sectionRecommendations || [])
            .filter((r) => !r.isDeleted)
            .map((r) => ({ ...r, isChanged: false, isNew: false })),
        })),
    );
  };

  const handleUpdate = async () => {
    try {
      await handleDeletions();
      await handleExistingUpdates();
      await handleTestDetailsSave();
      resetChangeFlags();
      toast.success("✅ Saved");
    } catch {
      toast.error("❌ Save failed");
    }
  };

  const validateForm = () => {
    const errs = [];
    if (!testDetails.testTitle.trim()) errs.push("Test title required");
    if (!testDetails.linkExpiryDate) errs.push("Expiry date required");
    if (!testDetails.testCategoryId) errs.push("Please select a category");
    const totalQ = sections.reduce(
      (n, s) => n + (s.questions?.filter((q) => !q.isDeleted).length || 0),
      0,
    );
    if (totalQ === 0) errs.push("Add at least one question");
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validateForm();
    if (errs.length) {
      errs.forEach((e) => toast.error(`⚠️ ${e}`));
      return;
    }
    setLoading(true);
    try {
      mode === "create" ? await handleCreate() : await handleUpdate();
    } finally {
      setLoading(false);
    }
  };

  // ── keyboard shortcut: Cmd/Ctrl+S to save ──────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testDetails, sections, globalInterpretations, globalRecommendations]);

  const visibleInterps = globalInterpretations.filter((i) => !i.isDeleted);
  const visibleRecs = globalRecommendations.filter((r) => !r.isDeleted);
  const globalBands = visibleInterps.map((interpItem, k) => {
    const recItem = visibleRecs[k] || blankGlobalRec();
    return {
      interpItem,
      recItem,
      interpAi: globalInterpretations.findIndex((x) => x === interpItem),
      recAi: globalRecommendations.findIndex((x) => x === recItem),
    };
  });
  const visibleSections = sections.filter((s) => !s.isDeleted);
  const testMaxScore = getTestMaxScore(sections);
  const hasUnsaved =
    testDetails.isChanged ||
    globalInterpretations.some((i) => i.isChanged) ||
    globalRecommendations.some((r) => r.isChanged) ||
    sections.some(
      (s) =>
        s.isNew ||
        s.isChanged ||
        (s.questions || []).some((q) => q.isNew || q.isChanged),
    );

  // ── derived: checklist / completion (presentation only) ───────────────────
  const totalQuestions = useMemo(
    () =>
      visibleSections.reduce(
        (n, s) => n + (s.questions?.filter((q) => !q.isDeleted).length || 0),
        0,
      ),
    [visibleSections],
  );

  const checklist = useMemo(
    () => [
      {
        key: "title",
        label: "Test title",
        done: !!testDetails.testTitle.trim(),
        step: "details",
      },
      {
        key: "expiry",
        label: "Expiry date",
        done: !!testDetails.linkExpiryDate,
        step: "details",
      },
      {
        key: "category",
        label: "Category",
        done: !!testDetails.testCategoryId,
        step: "details",
      },
      {
        key: "section",
        label: "At least one section",
        done: visibleSections.length > 0,
        step: "questions",
      },
      {
        key: "question",
        label: "At least one question",
        done: totalQuestions > 0,
        step: "questions",
      },
    ],
    [testDetails, visibleSections.length, totalQuestions],
  );
  const completion = Math.round(
    (checklist.filter((c) => c.done).length / checklist.length) * 100,
  );

  // Scoring and Settings have no required fields, so "done" for them can't
  // come from validation — it comes from the person actually having opened
  // the step. Otherwise the timeline would show them as complete before
  // anyone has looked at them, which is misleading.
  const stepDone = {
    details: checklist.filter((c) => c.step === "details").every((c) => c.done),
    questions: checklist
      .filter((c) => c.step === "questions")
      .every((c) => c.done),
    scoring: visitedSteps.has("scoring"),
    settings: visitedSteps.has("settings"),
  };

  const stepIdx = STEPS.findIndex((s) => s.key === activeStep);
  const isLastStep = stepIdx === STEPS.length - 1;
  const nextStep = STEPS[stepIdx + 1];
  const prevStep = STEPS[stepIdx - 1];

  const scrollToSection = (sectionId, idx) => {
    setActiveStep("questions");
    setMobileNavOpen(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(
        `section-${sectionId || `new-${idx}`}`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const scrollToQuestion = (section, sectionIndex, questionIndex) => {
    const sectionKey = section.sectionId || `new-${sectionIndex}`;
    setActiveStep("questions");
    setMobileNavOpen(false);
    setSidebarQuestionFocus({
      sectionKey,
      questionIndex,
      token: Date.now(),
    });
  };

  const toggleOutlineSection = (sectionKey) => {
    setExpandedOutlineSections((current) => {
      const next = new Set(current);
      next.has(sectionKey) ? next.delete(sectionKey) : next.add(sectionKey);
      return next;
    });
  };

  if (loading && mode === "edit") return <LoadingScreen message="Loading…" />;

  return (
    <div className="fb">
      {/* ── sticky top bar ──────────────────────────────────────────────────── */}
      <header className="fb__bar">
        <button
          type="button"
          className="fb__mobile-nav-toggle"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-label="Toggle navigation"
        >
          <Icon icon="mdi:menu" />
        </button>

        <button
          type="button"
          className="fb__back"
          onClick={onBack}
          title="Back to Test Management"
        >
          <Icon icon="mdi:arrow-left" />
        </button>

        <div className="fb__bar-title-wrap">
          {titleEditing ? (
            <input
              ref={titleInputRef}
              className="fb__bar-title-input"
              value={testDetails.testTitle}
              placeholder="Untitled test"
              autoFocus
              onChange={(e) => setField("testTitle", e.target.value)}
              onBlur={() => setTitleEditing(false)}
              onKeyDown={(e) => e.key === "Enter" && setTitleEditing(false)}
            />
          ) : (
            <button
              type="button"
              className="fb__bar-title"
              onClick={() => setTitleEditing(true)}
              title="Click to rename"
            >
              {testDetails.testTitle || "Untitled test"}
              <Icon icon="mdi:pencil-outline" className="fb__bar-title-edit" />
            </button>
          )}
          <span className="fb__bar-status">Draft</span>
        </div>

        <div className="fb__bar-right">
          <span
            className={`fb__save-indicator ${hasUnsaved ? "fb__save-indicator--unsaved" : ""}`}
          >
            <span className="fb__save-dot" />
            {hasUnsaved ? "Unsaved changes" : "All changes saved"}
          </span>

          <ButtonWithIcon
            className="edit-btn small"
            icon="mdi:eye-outline"
            onClick={() => setPreviewOpen(true)}
            text="Preview"
            type="button"
          />

          {mode === "edit" && (
            <IconButtonInline
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete test"
            />
          )}
        </div>
      </header>

      <div
        className={`fb__shell ${mobileNavOpen ? "fb__shell--nav-open" : ""}`}
      >
        {/* ── left sidebar ──────────────────────────────────────────────────── */}
        <aside className="fb__sidebar">
          <div className="fb__progress">
            <div className="fb__progress-ring">
              <svg viewBox="0 0 40 40">
                <circle cx="20" cy="20" r="17" className="fb__ring-bg" />
                <circle
                  cx="20"
                  cy="20"
                  r="17"
                  className="fb__ring-fg"
                  style={{
                    strokeDasharray: 2 * Math.PI * 17,
                    strokeDashoffset: 2 * Math.PI * 17 * (1 - completion / 100),
                  }}
                />
              </svg>
              <span>{completion}%</span>
            </div>
            <div>
              <p className="fb__progress-title">Test readiness</p>
              <p className="fb__progress-copy">
                {totalQuestions} question{totalQuestions !== 1 ? "s" : ""} ·{" "}
                {visibleSections.length} section
                {visibleSections.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <nav className="fb__nav fb__nav--timeline">
            {STEPS.map((s, i) => {
              const done = stepDone[s.key];
              const active = activeStep === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={[
                    "fb__nav-item",
                    active && "fb__nav-item--active",
                    done && "fb__nav-item--done",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    setActiveStep(s.key);
                    setMobileNavOpen(false);
                  }}
                >
                  <span className="fb__nav-node">
                    {done ? (
                      <Icon icon="mdi:check" />
                    ) : (
                      <span className="fb__nav-num">{i + 1}</span>
                    )}
                  </span>
                  <span className="fb__nav-copy">
                    <Icon icon={s.icon} className="fb__nav-icon" />
                    <span>{s.label}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          {activeStep === "questions" && visibleSections.length > 0 && (
            <div className="fb__outline">
              <p className="fb__outline-title">Sections</p>
              {visibleSections.map((s, i) => {
                const sectionKey = s.sectionId || `new-${i}`;
                const questions = (s.questions || []).filter(
                  (question) => !question.isDeleted,
                );
                const expanded = expandedOutlineSections.has(sectionKey);
                return (
                  <div key={sectionKey} className="fb__outline-section">
                    <div className="fb__outline-section-row">
                      <button
                        type="button"
                        className="fb__outline-item"
                        onClick={() => scrollToSection(s.sectionId, i)}
                      >
                        <span className="fb__outline-dot" />
                        <span className="fb__outline-label">
                          {s.sectionTitle?.trim() || `Section ${i + 1}`}
                        </span>
                        <span className="fb__outline-count">
                          {questions.length}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="fb__outline-toggle"
                        onClick={() => toggleOutlineSection(sectionKey)}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? "Hide" : "Show"} questions in ${s.sectionTitle?.trim() || `Section ${i + 1}`}`}
                      >
                        <Icon
                          icon={
                            expanded ? "mdi:chevron-up" : "mdi:chevron-down"
                          }
                        />
                      </button>
                    </div>
                    {expanded && (
                      <div className="fb__outline-questions">
                        {questions.map((question) => {
                          const questionIndex = s.questions.findIndex(
                            (item) => item === question,
                          );
                          return (
                            <button
                              key={question.questionId || questionIndex}
                              type="button"
                              className="fb__outline-question"
                              onClick={() =>
                                scrollToQuestion(s, i, questionIndex)
                              }
                            >
                              <span>Q{questionIndex + 1}</span>
                              {question.questionText?.trim() ||
                                "Untitled question"}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="fb__checklist">
            <p className="fb__outline-title">What's missing</p>
            {checklist.filter((c) => !c.done).length === 0 ? (
              <p className="fb__checklist-done">
                <Icon icon="mdi:check-circle" /> Everything looks good
              </p>
            ) : (
              checklist
                .filter((c) => !c.done)
                .map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className="fb__checklist-item"
                    onClick={() => setActiveStep(c.step)}
                  >
                    <Icon icon="mdi:circle-outline" />
                    {c.label}
                  </button>
                ))
            )}
          </div>
        </aside>

        <button
          type="button"
          className="fb__nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
        />

        {/* ── center workspace ─────────────────────────────────────────────── */}
        <main className="fb__workspace">
          {activeStep === "details" && (
            <div className="fb__panel" key="details">
              <div className="fb__panel-hd">
                <h1>Basic Details</h1>
                <p>
                  The essentials respondents and admins will see about this
                  test.
                </p>
              </div>

              <section className="fb__card">
                <h2 className="fb__card-title">Test Info</h2>
                <div className="fb__row">
                  <div className="fb__field fb__field--grow">
                    <label>
                      Title <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Personality Baseline"
                      value={testDetails.testTitle}
                      onChange={(e) => setField("testTitle", e.target.value)}
                    />
                  </div>
                  <div className="fb__field">
                    <label>
                      Expiry <span className="req">*</span>
                    </label>
                    <input
                      type="date"
                      value={testDetails.linkExpiryDate}
                      onChange={(e) =>
                        setField("linkExpiryDate", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="fb__field">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    placeholder="Brief description (optional)"
                    value={testDetails.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>

                <div className="fb__row">
                  <div className="fb__field fb__field--grow">
                    <label>
                      Category <span className="req">*</span>
                    </label>
                    <select
                      value={testDetails.testCategoryId}
                      onChange={(e) =>
                        setField("testCategoryId", e.target.value)
                      }
                    >
                      <option value="">— Select category —</option>
                      {categories.map((cat) => (
                        <option
                          key={cat.testCategoryId}
                          value={cat.testCategoryId}
                        >
                          {cat.testCategoryType.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {isAdmin && (
                  <div className="fb__row">
                    <div className="fb__field">
                      <Toggle
                        label="Save as template"
                        hint="Off saves this as a regular user test"
                        value={testDetails.testType === "TEMPLATE"}
                        onChange={(v) =>
                          setField("testType", v ? "TEMPLATE" : "USER")
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="fb__row">
                  <div className="fb__field fb__field--duration">
                    <label>Duration</label>
                    <Toggle
                      label="Set a time limit"
                      hint="Off means respondents get unlimited time"
                      value={durationEnabled}
                      onChange={(v) => {
                        setDurationEnabled(v);
                        if (!v) {
                          setField("durationHours", 0);
                          setField("durationMinutes", 0);
                        }
                      }}
                    />
                    {durationEnabled && (
                      <div className="fb__dur">
                        <input
                          type="number"
                          min={0}
                          max={23}
                          value={testDetails.durationHours}
                          onChange={(e) =>
                            setField("durationHours", e.target.value)
                          }
                        />
                        <span>h</span>
                        <input
                          type="number"
                          min={0}
                          max={59}
                          value={testDetails.durationMinutes}
                          onChange={(e) =>
                            setField("durationMinutes", e.target.value)
                          }
                        />
                        <span>m</span>
                      </div>
                    )}
                  </div>
                  <div className="fb__field">
                    <label>Attempts</label>
                    <div className="fb__radio-group">
                      {[
                        ["ONE_TIME", "Single"],
                        ["MULTIPLE", "Multiple"],
                        ["UNLIMITED", "Unlimited"],
                      ].map(([val, lbl]) => (
                        <label
                          key={val}
                          className={`fb__radio ${testDetails.testAttemptType === val ? "fb__radio--on" : ""}`}
                        >
                          <input
                            type="radio"
                            name="attemptType"
                            value={val}
                            checked={testDetails.testAttemptType === val}
                            onChange={() => setField("testAttemptType", val)}
                          />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                  {testDetails.testAttemptType === "MULTIPLE" && (
                    <div className="fb__field fb__field--xs">
                      <label>Max</label>
                      <input
                        type="number"
                        min={1}
                        value={testDetails.maxAttempts}
                        onChange={(e) =>
                          setField("maxAttempts", e.target.value)
                        }
                      />
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeStep === "questions" && (
            <div className="fb__panel" key="questions">
              <div className="fb__panel-hd fb__panel-hd--row">
                <div>
                  <h1>Questions</h1>
                  <p>
                    Organize your test into sections, each with its own
                    questions.
                  </p>
                </div>
                <div className="fb__search">
                  <Icon icon="mdi:magnify" />
                  <input
                    type="text"
                    placeholder="Search questions…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                    >
                      <Icon icon="mdi:close" />
                    </button>
                  )}
                </div>
              </div>

              {visibleSections.length === 0 ? (
                <div className="fb__empty-hero">
                  <div className="fb__empty-hero-icon">
                    <Icon icon="mdi:shape-outline" />
                  </div>
                  <h2>Let's build something meaningful.</h2>
                  <p>
                    Start with a section — a group of related questions — then
                    add questions inside it.
                  </p>
                  {!restrictToExisting && (
                    <ButtonWithIcon
                      className="add-btn fb__empty-cta"
                      icon="mdi:plus-circle-outline"
                      onClick={addSection}
                      text="Create your first section"
                      type="button"
                    />
                  )}
                </div>
              ) : (
                <>
                  {restrictToExisting && (
                    <p className="fb__restricted-note">
                      <Icon icon="mdi:lock-outline" /> This test has responses,
                      so you can edit existing sections and questions but can't
                      add or remove any.
                    </p>
                  )}
                  {sections
                    .filter((s) => !s.isDeleted)
                    .map((section, index) => (
                      <Section
                        key={section.sectionId || `new-${index}`}
                        index={index}
                        section={section}
                        searchQuery={searchQuery}
                        focusedQuestion={sidebarQuestionFocus}
                        onUpdate={(u) => updateSection(index, u)}
                        onDelete={() => deleteSection(index)}
                        restricted={restrictToExisting}
                      />
                    ))}

                  {!restrictToExisting && (
                    <ButtonWithIcon
                      className="add-btn fb__add-section-btn"
                      icon="mdi:plus-circle-outline"
                      onClick={addSection}
                      text="Add Section"
                      type="button"
                    />
                  )}
                </>
              )}
            </div>
          )}

          {activeStep === "scoring" && (
            <div className="fb__panel" key="scoring">
              <div className="fb__panel-hd">
                <h1>Scoring</h1>
                <p>
                  Overall score bands for the whole test. Section-level bands
                  live inside each section's Scoring tab.
                </p>
              </div>

              <section className="fb__card sec__scoring-summary">
                <div className="sec__scoring-total">
                  <strong>Total possible score for the whole test: </strong>
                  {testMaxScore}
                </div>
                <label className="sec__scoring-divider">
                  Divide into
                  <input
                    type="number"
                    min="1"
                    value={globalBandCount}
                    onChange={(e) =>
                      setGlobalBandCount(
                        Math.max(1, Number(e.target.value) || 1),
                      )
                    }
                  />
                  bands
                </label>
                {!restrictToExisting && (
                  <ButtonWithIcon
                    className="add small"
                    icon="mdi:auto-fix"
                    onClick={autoGenerateGlobalBands}
                    text="Auto-generate bands"
                    type="button"
                  />
                )}
              </section>

              <section className="fb__card">
                <div className="fb__card-hd">
                  <h2 className="fb__card-title">Score bands</h2>
                  {!restrictToExisting && (
                    <ButtonWithIcon
                      className="add small"
                      icon="mdi:plus-circle-outline"
                      onClick={addGlobalBand}
                      text="Add band"
                      type="button"
                    />
                  )}
                </div>
                {restrictToExisting && (
                  <p className="fb__restricted-note">
                    <Icon icon="mdi:lock-outline" /> This test has responses —
                    you can edit these bands but can't add or remove any.
                  </p>
                )}
                {globalBands.map(({ interpItem, recItem, interpAi, recAi }) => (
                  <BandCard
                    key={interpItem.interpretationId || `band-${interpAi}`}
                    range={interpItem}
                    onRangeChange={(field, val) =>
                      changeGlobalBandRange(interpAi, field, val)
                    }
                    interp={interpItem}
                    onInterpChange={(field, val) =>
                      changeGlobalInterpText(interpAi, field, val)
                    }
                    rec={recItem}
                    onRecChange={(field, val) =>
                      changeGlobalRecText(recAi, field, val)
                    }
                    onDelete={() => delGlobalBand(interpAi)}
                    showDelete={!restrictToExisting && globalBands.length > 1}
                  />
                ))}
              </section>
            </div>
          )}

          {activeStep === "settings" && (
            <div className="fb__panel" key="settings">
              <div className="fb__panel-hd">
                <h1>Settings</h1>
                <p>Control what respondents see after finishing the test.</p>
              </div>

              <section className="fb__card">
                <h2 className="fb__card-title">Result Visibility</h2>
                <div className="fb__toggles">
                  <Toggle
                    label="Show results to respondent"
                    hint="Master switch — turn off to hide all results below"
                    value={testDetails.showResultsToUser}
                    onChange={setShowResultsToUser}
                  />
                  {testDetails.showResultsToUser && (
                    <div className="fb__toggles-sub">
                      {[
                        ["showScore", "Score", "The numeric score"],
                        [
                          "showRecommendation",
                          "Recommendation",
                          "Overall test-level advice",
                        ],
                        [
                          "showInterpretation",
                          "Interpretation",
                          "Overall test-level meaning",
                        ],
                        [
                          "showSectionRecommendation",
                          "Section recommendation",
                          "Advice per section",
                        ],
                        [
                          "showSectionInterpretation",
                          "Section interpretation",
                          "Meaning per section",
                        ],
                      ].map(([key, lbl, hint]) => (
                        <Toggle
                          key={key}
                          label={lbl}
                          hint={hint}
                          value={testDetails[key]}
                          onChange={(v) => setField(key, v)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>

              {mode === "edit" && (
                <section className="fb__card fb__card--danger">
                  <h2 className="fb__card-title">Danger zone</h2>
                  <div className="fb__danger-row">
                    <div>
                      <p className="fb__danger-title">Delete this test</p>
                      <p className="fb__danger-copy">
                        Permanently remove "
                        {testDetails.testTitle || "this test"}" and all its
                        data. This cannot be undone.
                      </p>
                    </div>
                    <ButtonWithIcon
                      className="danger"
                      icon="material-symbols-light:delete-outline"
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      text="Delete test"
                    />
                  </div>
                </section>
              )}
            </div>
          )}

          <div className="fb__final-bar">
            <div>
              <p className="fb__final-title">
                {isLastStep
                  ? mode === "create"
                    ? "Ready to create?"
                    : "Ready to save?"
                  : `Step ${stepIdx + 1} of ${STEPS.length}`}
              </p>
              <p className="fb__final-copy">
                {isLastStep
                  ? completion === 100
                    ? "Everything's set."
                    : `${checklist.filter((c) => !c.done).length} item(s) left to complete.`
                  : `Next up: ${nextStep?.label}`}
              </p>
            </div>
            <div className="fb__final-actions">
              {prevStep && (
                <ButtonWithIcon
                  className="edit-btn small"
                  icon="mdi:arrow-left"
                  type="button"
                  onClick={() => setActiveStep(prevStep.key)}
                  text="Previous section"
                />
              )}
              {!isLastStep && (
                <ButtonWithIcon
                  className="add-btn"
                  icon="mdi:arrow-right"
                  type="button"
                  onClick={() => setActiveStep(nextStep.key)}
                  text="Next section"
                />
              )}
              {isLastStep && (
                <ButtonWithIcon
                  className="add-btn"
                  icon={
                    mode === "create"
                      ? "mdi:plus-circle-outline"
                      : "mdi:content-save-outline"
                  }
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  text={
                    loading
                      ? "Saving…"
                      : mode === "create"
                        ? "Create Test"
                        : "Save Changes"
                  }
                />
              )}
            </div>
          </div>
        </main>
      </div>

      {/* ── preview modal ────────────────────────────────────────────────── */}
      {previewOpen && (
        <div
          className="fb__preview-overlay"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="fb__preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fb__preview-hd">
              <h2>
                <Icon icon="mdi:eye-outline" /> Respondent preview
              </h2>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                aria-label="Close preview"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
            <div className="fb__preview-body">
              <h1>{testDetails.testTitle || "Untitled test"}</h1>
              {testDetails.description && (
                <p className="fb__preview-desc">{testDetails.description}</p>
              )}
              <div className="fb__preview-meta">
                {testDetails.durationHours > 0 ||
                testDetails.durationMinutes > 0 ? (
                  <span>
                    <Icon icon="mdi:clock-outline" />{" "}
                    {testDetails.durationHours}h {testDetails.durationMinutes}m
                  </span>
                ) : (
                  <span>
                    <Icon icon="mdi:clock-outline" /> No time limit
                  </span>
                )}
                <span>
                  <Icon icon="mdi:format-list-checks" /> {totalQuestions}{" "}
                  questions
                </span>
              </div>

              {visibleSections.length === 0 && (
                <p className="fb__preview-empty">No sections added yet.</p>
              )}

              {visibleSections.map((s, si) => (
                <div
                  className="fb__preview-section"
                  key={s.sectionId || `ps-${si}`}
                >
                  <h3>{s.sectionTitle?.trim() || `Section ${si + 1}`}</h3>
                  {(s.questions || [])
                    .filter((q) => !q.isDeleted)
                    .map((q, qi) => {
                      const meta = getQuestionTypeMeta(q.answerType);
                      const opts = (q.answerOptions || []).filter(
                        (o) => !o.isDeleted,
                      );
                      return (
                        <div
                          className="fb__preview-q"
                          key={q.questionId || `pq-${qi}`}
                        >
                          <p>
                            <span
                              className="fb__preview-q-badge"
                              style={{
                                background: meta.colorSoft,
                                color: meta.color,
                              }}
                            >
                              <Icon icon={meta.icon} />
                            </span>
                            {q.questionText?.trim() || "Untitled question"}
                          </p>
                          <div className="fb__preview-q-opts">
                            {opts.slice(0, 6).map((o, oi) => (
                              <span key={oi}>
                                {o.answerText || `Option ${oi + 1}`}
                              </span>
                            ))}
                            {opts.length > 6 && (
                              <span>+{opts.length - 6} more</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <WarningPopup
        isOpen={showDeleteConfirm}
        title="Delete Test"
        message={`Delete "${testDetails.testTitle}"? This cannot be undone.`}
        acceptText="Delete"
        declineText="Cancel"
        onAccept={handleDeleteConfirm}
        onDecline={() => setShowDeleteConfirm(false)}
        onClose={() => setShowDeleteConfirm(false)}
      />

      <WarningPopup
        isOpen={showTakenNotice}
        title="This test already has responses"
        message="One or more people have already taken this test, so editing it may affect their scores and results. You can still update the wording, options, scores, interpretations and recommendations that already exist, but adding or removing questions, sections, score bands, interpretations or recommendations is disabled to keep past results consistent."
        acceptText="Got it, continue editing"
        declineText="Go Back"
        onAccept={() => setShowTakenNotice(false)}
        onDecline={() => (onBack ? onBack() : navigate("/testmanagement"))}
        onClose={() => setShowTakenNotice(false)}
      />
    </div>
  );
};

export default FormBuilder;
